// Indici custom: media pesata di componenti portate su 0–100 con una scala [min, max].
// Le definizioni sono dati: oggi i valori iniziali, poi la tabella di Impostazioni (contratto tappa 5).

import {per90, ratio, sumMatches} from "@/utils/wyscoutImport";

// kind: "per90" → num/90'; "ratio" → num/den in %; "diff90" → (num − sub)/90'.
const c = (label, weight, kind, num, min, max, extra = {}) => ({
	label,
	weight,
	kind,
	num,
	min,
	max,
	...extra,
});

export const DEFAULT_INDICES = [
	{
		id: "AF",
		label: "Finalizzazione",
		attitude: "AF",
		group: "outfield",
		components: [
			c("Gol/90", 30, "per90", "goals", 0, 0.7),
			c("xG/90", 30, "per90", "xg", 0, 0.6),
			c("Gol/tiri", 20, "ratio", "goals", 5, 25, {den: "shots"}),
			c("Tiri in porta", 20, "ratio", "shotsOnTarget", 25, 55, {den: "shots"}),
		],
	},
	{
		id: "AR",
		label: "Rifinitura",
		attitude: "AR",
		group: "outfield",
		components: [
			c("xA/90", 35, "per90", "xa", 0, 0.3),
			c("Passaggi per il tiro/90", 30, "per90", "shotAssists", 0, 1.5),
			c("Passaggi in area riusciti/90", 20, "per90", "passesToBoxOk", 0, 3),
			c("Filtranti riusciti/90", 15, "per90", "throughPassesOk", 0, 0.8),
		],
	},
	{
		id: "R",
		label: "Regia",
		attitude: "R",
		group: "outfield",
		components: [
			c("Passaggi riusciti", 30, "ratio", "passesOk", 65, 90, {den: "passes"}),
			c(
				"Passaggi in avanti riusciti/90",
				25,
				"per90",
				"forwardPassesOk",
				3,
				20,
			),
			c("Passaggi nella trequarti/90", 25, "per90", "finalThirdPasses", 1, 10),
			c("Palloni ricevuti/90", 20, "per90", "receivedPasses", 10, 50),
		],
	},
	{
		id: "D",
		label: "Lavoro senza palla",
		attitude: "D",
		group: "outfield",
		components: [
			c("Recuperi/90", 30, "per90", "recoveries", 1, 7),
			c("Intercetti/90", 25, "per90", "interceptions", 0, 3),
			c("Duelli difensivi vinti/90", 25, "per90", "defensiveDuelsWon", 0.5, 5),
			c("Duelli difensivi vinti", 20, "ratio", "defensiveDuelsWon", 35, 70, {
				den: "defensiveDuels",
			}),
		],
	},
	{
		id: "DUELS",
		label: "Duelli",
		group: "outfield",
		components: [
			c("Duelli vinti", 40, "ratio", "duelsWon", 35, 55, {den: "duels"}),
			c("Aerei vinti", 30, "ratio", "aerialDuelsWon", 20, 60, {
				den: "aerialDuels",
			}),
			c("Duelli offensivi vinti", 30, "ratio", "offensiveDuelsWon", 25, 50, {
				den: "offensiveDuels",
			}),
		],
	},
	{
		id: "GK_SAVES",
		label: "Parate",
		group: "goalkeeper",
		components: [
			c("Parate", 60, "ratio", "saves", 55, 80, {den: "shotsAgainst"}),
			c("xCG − gol subiti/90", 40, "diff90", "xcg", -0.3, 0.3, {
				sub: "goalsConceded",
			}),
		],
	},
	{
		id: "GK_EXITS",
		label: "Uscite",
		group: "goalkeeper",
		components: [c("Uscite/90", 100, "per90", "exits", 0, 1.5)],
	},
	{
		id: "GK_FEET",
		label: "Gioco coi piedi",
		group: "goalkeeper",
		components: [
			c("Passaggi riusciti", 50, "ratio", "passesOk", 60, 90, {den: "passes"}),
			c("Lanci lunghi riusciti", 50, "ratio", "longPassesOk", 30, 65, {
				den: "longPasses",
			}),
		],
	},
];

export const scaleScore = (value, min, max) => {
	if (value === null || !Number.isFinite(value) || max === min) return null;
	const score = ((value - min) / (max - min)) * 100;
	return Math.max(0, Math.min(100, score));
};

export const componentValue = (component, totals) => {
	const num = totals?.[component.num];
	if (num === undefined) return null;
	switch (component.kind) {
		case "per90":
			return per90(num, totals?.minutes);
		case "ratio":
			return ratio(num, totals?.[component.den]);
		case "diff90":
			return per90(
				(num ?? 0) - (totals?.[component.sub] ?? 0),
				totals?.minutes,
			);
		default:
			return null;
	}
};

// Valore 0–100 dell'indice; le componenti non calcolabili escono e i pesi si ridistribuiscono.
export const computeIndex = (index, totals) => {
	const components = index.components.map((comp) => {
		const value = componentValue(comp, totals);
		return {...comp, value, score: scaleScore(value, comp.min, comp.max)};
	});
	const valid = components.filter((comp) => comp.score !== null);
	const weight = valid.reduce((sum, comp) => sum + comp.weight, 0);
	const score = weight
		? valid.reduce((sum, comp) => sum + comp.score * comp.weight, 0) / weight
		: null;
	return {...index, score, components};
};

// Indice per stagione e media di carriera pesata sui minuti.
export const indexHistory = (index, matches, seasons) => {
	const bySeason = (seasons || []).map((season) => {
		const totals = sumMatches(
			(matches || []).filter((m) => m.season === season),
		);
		return {
			season,
			minutes: totals.minutes,
			score: computeIndex(index, totals).score,
		};
	});
	const counted = bySeason.filter((s) => s.score !== null && s.minutes > 0);
	const minutes = counted.reduce((sum, s) => sum + s.minutes, 0);
	const career = minutes
		? counted.reduce((sum, s) => sum + s.score * s.minutes, 0) / minutes
		: null;
	return {bySeason, career};
};

export const indicesFor = (goalkeeper, indices = DEFAULT_INDICES) =>
	indices.filter((i) => i.group === (goalkeeper ? "goalkeeper" : "outfield"));

export const INDEX_GROUPS = [
	{value: "outfield", label: "Giocatori di movimento"},
	{value: "goalkeeper", label: "Portieri"},
];

export const COMPONENT_KINDS = [
	{value: "per90", label: "Valore per 90'"},
	{value: "ratio", label: "Percentuale (A su B)"},
	{value: "diff90", label: "Differenza per 90' (A − B)"},
];

export const weightsTotal = (index) =>
	(index?.components || []).reduce(
		(sum, c) => sum + (Number(c.weight) || 0),
		0,
	);

// Errori: "label", "components", "weights" oppure l'indice della componente sbagliata.
export const validateIndex = (index) => {
	const errors = [];
	if (!String(index?.label || "").trim()) errors.push("label");
	const components = index?.components || [];
	if (!components.length) errors.push("components");
	else if (weightsTotal(index) !== 100) errors.push("weights");
	components.forEach((comp, i) => {
		const min = Number(comp.min),
			max = Number(comp.max);
		if (
			!String(comp.label || "").trim() ||
			!comp.num ||
			!(Number(comp.weight) > 0) ||
			!Number.isFinite(min) ||
			!Number.isFinite(max) ||
			min >= max ||
			(comp.kind === "ratio" && !comp.den) ||
			(comp.kind === "diff90" && !comp.sub)
		)
			errors.push(i);
	});
	return errors;
};

const formatNumber = (value) => String(value).replace(".", ",");

// "30% Gol/90 [0–0,7] + 20% Gol/tiri [5–25%]"
export const indexFormula = (index) =>
	(index?.components || [])
		.map(
			(c) =>
				`${c.weight}% ${c.label} [${formatNumber(c.min)}–${formatNumber(c.max)}${c.kind === "ratio" ? "%" : ""}]`,
		)
		.join(" + ");
